import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AppNavbar from './AppNavbar';
import Header from './Header';
import SideMenu from './SideMenu';
import AppTheme from '../../shared-theme/AppTheme';
import Copyright from '../internals/components/Copyright';
import { useGrow, GROW_STAGES } from '../context/GrowContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// Stage colors matching GrowProgressBar
const STAGE_COLORS = {
  seedling: '#22c55e',
  vegetative: '#3b82f6',
  flower: '#ec4899',
  lateFlower: '#f97316',
};

// Helper to calculate grow duration in days
function calculateDuration(startDate) {
  const start = new Date(startDate);
  const end = new Date();
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

// Render status chip
function renderStatus(status, size = 'small') {
  const statusConfig = {
    active: { label: 'Active', color: 'success' },
    completed: { label: 'Completed', color: 'default', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    cancelled: { label: 'Cancelled', color: 'warning' },
    paused: { label: 'Paused', color: 'warning' },
  };
  const config = statusConfig[status] || statusConfig.active;
  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      icon={config.icon}
      sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
    />
  );
}

// Mobile Grow Card component
function MobileGrowCard({ row, onNavigate }) {
  const isCompleted = row.status === 'completed';

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        borderColor: isCompleted ? 'success.main' : 'divider',
        borderWidth: isCompleted ? 2 : 1,
      }}
    >
      <CardActionArea onClick={() => onNavigate(row.id)} sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          {/* Header: Name and Status */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {row.presetName}
              </Typography>
            </Box>
            {renderStatus(row.status)}
          </Stack>

          {/* Progress Bar */}
          {row.progressData && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
                  {row.daysInfo} of {row.progressData.totalDuration} days
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    color: isCompleted ? 'success.main' : 'primary.main',
                  }}
                >
                  {Math.round(row.progressData.progress)}%
                </Typography>
              </Stack>
              <Box sx={{ position: 'relative', height: 8, borderRadius: 1, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ height: '100%' }}>
                  {row.progressData.stages.map((stage, index) => {
                    const widthPercent = (stage.duration / row.progressData.totalDuration) * 100;
                    const isStageCompleted = index < row.progressData.currentStageIndex;
                    const isCurrent = index === row.progressData.currentStageIndex;

                    return (
                      <Box
                        key={stage.id}
                        sx={{
                          width: `${widthPercent}%`,
                          height: '100%',
                          bgcolor: isStageCompleted || isCompleted
                            ? STAGE_COLORS[stage.id]
                            : isCurrent
                            ? `${STAGE_COLORS[stage.id]}40`
                            : 'action.hover',
                          position: 'relative',
                          borderRight: index < row.progressData.stages.length - 1 ? '1px solid' : 'none',
                          borderColor: 'background.paper',
                        }}
                      >
                        {isCurrent && !isCompleted && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: `${row.progressData.currentStageProgress * 100}%`,
                              height: '100%',
                              bgcolor: STAGE_COLORS[stage.id],
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Box>
          )}

          {/* Footer: Stage and Date */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {renderStage(row.currentStage)}
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
              Started: {row.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

// Render stage chip with matching colors
function renderStage(stage) {
  const stageInfo = GROW_STAGES[stage];
  if (!stageInfo) return null;

  return (
    <Chip
      label={stageInfo.label}
      size="small"
      sx={{
        bgcolor: `${STAGE_COLORS[stage]}20`,
        color: STAGE_COLORS[stage],
        fontWeight: 600,
        fontFamily: 'Inter, sans-serif',
        border: `1px solid ${STAGE_COLORS[stage]}40`,
      }}
    />
  );
}

// Custom progress bar matching GrowProgressBar style
function renderProgressBar(params) {
  const { progress, stages, currentStageIndex, currentStageProgress, totalDuration } = params.value || {};

  if (!stages || stages.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>-</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', px: 1 }}>
      {/* Multi-segment progress bar */}
      <Box sx={{ position: 'relative', height: 12, flex: 1, minWidth: 100 }}>
        <Stack direction="row" sx={{ height: '100%', borderRadius: 1, overflow: 'hidden' }}>
          {stages.map((stage, index) => {
            const widthPercent = (stage.duration / totalDuration) * 100;
            const isCompleted = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;

            return (
              <Box
                key={stage.id}
                sx={{
                  width: `${widthPercent}%`,
                  height: '100%',
                  bgcolor: isCompleted
                    ? STAGE_COLORS[stage.id]
                    : isCurrent
                    ? `${STAGE_COLORS[stage.id]}40`
                    : 'action.hover',
                  position: 'relative',
                  borderRight: index < stages.length - 1 ? '1px solid' : 'none',
                  borderColor: 'background.paper',
                }}
              >
                {isCurrent && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${currentStageProgress * 100}%`,
                      height: '100%',
                      bgcolor: STAGE_COLORS[stage.id],
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Stack>
        {/* Current position indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            left: `${progress}%`,
            transform: 'translateX(-50%)',
            width: 2,
            height: 16,
            bgcolor: 'text.primary',
            borderRadius: 0.5,
          }}
        />
      </Box>
      {/* Percentage */}
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          color: 'primary.main',
          minWidth: 36,
          textAlign: 'right',
        }}
      >
        {Math.round(progress)}%
      </Typography>
    </Box>
  );
}

function GrowLogContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { grows, presets, setActiveGrowId, getGrowProgress } = useGrow();

  // Determine if we're in demo mode based on the URL
  const isDemo = location.pathname.startsWith('/demo');
  const basePath = isDemo ? '/demo' : '/app';

  // Transform grows into rows for the DataGrid
  const rows = React.useMemo(() => {
    return grows.map((grow) => {
      const preset = presets.find(p => p.id === grow.presetId);
      const progress = getGrowProgress(grow.id);

      return {
        id: grow.id,
        name: grow.name,
        presetName: preset?.name || 'Unknown Preset',
        startDate: new Date(grow.startDate),
        currentStage: grow.currentStage,
        duration: calculateDuration(grow.startDate),
        progressData: progress ? {
          progress: progress.overallProgress * 100,
          stages: progress.stages,
          currentStageIndex: progress.currentStageIndex,
          currentStageProgress: progress.currentStageProgress,
          totalDuration: progress.totalDuration,
        } : null,
        status: grow.status || 'active',
        daysInfo: progress ? `Day ${progress.totalDaysFromStart}` : '-',
      };
    });
  }, [grows, presets, getGrowProgress]);

  const handleNavigateToGrow = (growId) => {
    setActiveGrowId(growId);
    navigate(`${basePath}/grow/${growId}`);
  };

  const handleNewGrow = () => {
    navigate(`${basePath}?new=true`);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Grow Name',
      flex: 1.2,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'presetName',
      headerName: 'Preset',
      flex: 1,
      minWidth: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Inter, sans-serif',
            color: 'text.secondary',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'startDate',
      headerName: 'Started',
      flex: 0.8,
      minWidth: 110,
      headerAlign: 'center',
      align: 'center',
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      },
    },
    {
      field: 'currentStage',
      headerName: 'Stage',
      flex: 0.8,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => renderStage(params.value),
    },
    {
      field: 'daysInfo',
      headerName: 'Days',
      flex: 0.5,
      minWidth: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'progressData',
      headerName: 'Progress',
      flex: 1.5,
      minWidth: 200,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: renderProgressBar,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => renderStatus(params.value),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.4,
      minWidth: 60,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Open grow dashboard">
          <IconButton
            size="small"
            onClick={() => handleNavigateToGrow(params.row.id)}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* Header with breadcrumbs, calendar, etc */}
      <Header />

      {/* Page Title and Actions */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2, mb: 3 }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
          }}
        >
          Grow Log
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewGrow}
        >
          New Grow
        </Button>
      </Stack>

      {/* Summary Stats - scrollable on mobile */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 3,
        overflowX: { xs: 'auto', md: 'visible' },
        pb: { xs: 1, md: 0 },
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'action.hover', borderRadius: 2 },
      }}>
        <Card
          variant="outlined"
          sx={{
            p: { xs: 1.5, md: 2 },
            minWidth: { xs: 100, md: 130 },
            flexShrink: 0,
            bgcolor: 'success.main',
            borderColor: 'success.main',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              color: 'white',
              fontSize: { xs: '1.5rem', md: '2.125rem' },
            }}
          >
            {rows.filter(r => r.status === 'active').length}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
            Active
          </Typography>
        </Card>
        <Card
          variant="outlined"
          sx={{
            p: { xs: 1.5, md: 2 },
            minWidth: { xs: 100, md: 130 },
            flexShrink: 0,
            bgcolor: 'action.selected',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              color: 'success.main',
              fontSize: { xs: '1.5rem', md: '2.125rem' },
            }}
          >
            {rows.filter(r => r.status === 'completed').length}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
            Completed
          </Typography>
        </Card>
        <Card
          variant="outlined"
          sx={{
            p: { xs: 1.5, md: 2 },
            minWidth: { xs: 100, md: 130 },
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '2.125rem' },
            }}
          >
            {rows.length}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
            Total
          </Typography>
        </Card>
      </Box>

      {/* Mobile: Card Layout, Desktop: Data Grid */}
      {rows.length > 0 ? (
        isMobile ? (
          // Mobile card layout
          <Box>
            {/* Section: Active Grows */}
            {rows.filter(r => r.status === 'active').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    mb: 1.5,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: 0.5,
                  }}
                >
                  Active Grows
                </Typography>
                {rows
                  .filter(r => r.status === 'active')
                  .sort((a, b) => b.startDate - a.startDate)
                  .map(row => (
                    <MobileGrowCard key={row.id} row={row} onNavigate={handleNavigateToGrow} />
                  ))}
              </Box>
            )}

            {/* Section: Completed Grows */}
            {rows.filter(r => r.status === 'completed').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    mb: 1.5,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: 0.5,
                  }}
                >
                  Completed Grows
                </Typography>
                {rows
                  .filter(r => r.status === 'completed')
                  .sort((a, b) => b.startDate - a.startDate)
                  .map(row => (
                    <MobileGrowCard key={row.id} row={row} onNavigate={handleNavigateToGrow} />
                  ))}
              </Box>
            )}

            {/* Section: Cancelled Grows */}
            {rows.filter(r => r.status === 'cancelled').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    mb: 1.5,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: 0.5,
                  }}
                >
                  Cancelled Grows
                </Typography>
                {rows
                  .filter(r => r.status === 'cancelled')
                  .sort((a, b) => b.startDate - a.startDate)
                  .map(row => (
                    <MobileGrowCard key={row.id} row={row} onNavigate={handleNavigateToGrow} />
                  ))}
              </Box>
            )}
          </Box>
        ) : (
          // Desktop DataGrid
          <Card variant="outlined" sx={{ height: 500 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                sorting: {
                  sortModel: [{ field: 'startDate', sort: 'desc' }],
                },
              }}
              pageSizeOptions={[10, 20, 50]}
              disableRowSelectionOnClick
              density="comfortable"
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: 'action.hover',
                  borderRadius: 0,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                },
                '& .MuiDataGrid-row.even': {
                  bgcolor: 'transparent',
                },
                '& .MuiDataGrid-row.odd': {
                  bgcolor: 'action.hover',
                },
              }}
              slotProps={{
                filterPanel: {
                  filterFormProps: {
                    logicOperatorInputProps: {
                      variant: 'outlined',
                      size: 'small',
                    },
                    columnInputProps: {
                      variant: 'outlined',
                      size: 'small',
                      sx: { mt: 'auto' },
                    },
                    operatorInputProps: {
                      variant: 'outlined',
                      size: 'small',
                      sx: { mt: 'auto' },
                    },
                    valueInputProps: {
                      InputComponentProps: {
                        variant: 'outlined',
                        size: 'small',
                      },
                    },
                  },
                },
              }}
            />
          </Card>
        )
      ) : (
        <Card
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Inter, sans-serif',
              color: 'text.secondary',
              mb: 2,
            }}
          >
            No grows recorded yet
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 3,
            }}
          >
            Start your first grow to begin tracking your cultivation journey.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={handleNewGrow}
          >
            Start Your First Grow
          </Button>
        </Card>
      )}

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}

export default function GrowLogPage(props) {
  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <GrowLogContent />
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
